import * as R from 'ramda'
import { computed, ComputedRef, reactive } from 'vue'

import { isIncidentDeleted } from '@collector/shared-ui-domain'
import { EventStatsLevel } from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { useEvent, useSportMaps } from '@desktop/views/Relation/composables'
import { useBetIncidents } from '@desktop/views/Relation/Sports/composables'
import {
  injectEventClock,
  injectHistoryIncidentsConfig,
  injectIncidentsCustomFiltersConfig,
  injectVIPIncidentsConfig,
} from '@desktop/views/Relation/Sports/provide-inject'

import { IncidentFilterToggle } from '../../types'
import {
  isIncidentEmpty as _isIncidentEmpty,
  isIncidentImportant as _isIncidentImportant,
  isVIPIncident as _isVIPIncident,
} from './filterFunctions'

export const SameTime = 'same time'

type UseIncidentFiltersToggle = {
  incidentFiltersToggle: ComputedRef<IncidentFilterToggle[]>
}

export function useIncidentFiltersToggle(
  hasSameTimeIncidents: (incident: Incident) => boolean,
): UseIncidentFiltersToggle {
  // inject
  const historyIncidentsConfig = injectHistoryIncidentsConfig() || {}
  const vipIncidentsConfig = injectVIPIncidentsConfig()
  const incidentsCustomFiltersConfig = injectIncidentsCustomFiltersConfig()
  const eventClock = injectEventClock()

  // composables
  const { incidentIdToSportIncidentMap } = useSportMaps()
  const { isIncidentEmergencyOrBet } = useBetIncidents()
  const { event } = useEvent()

  // computed
  const shouldShowVIPIncidentsFilter = computed(
    () =>
      event.value.event_stats_lvl === EventStatsLevel.VIP && vipIncidentsConfig,
  )

  // data
  const isIncidentImportant = R.partialRight(_isIncidentImportant, [
    incidentIdToSportIncidentMap.value,
  ])

  const isIncidentEmpty = R.partialRight(_isIncidentEmpty, [
    historyIncidentsConfig,
  ])

  const isVIPIncident = R.partialRight(_isVIPIncident, [vipIncidentsConfig])

  const staticFilters: IncidentFilterToggle[] = [
    {
      name: 'important',
      filterFunction: isIncidentImportant,
    },
    {
      name: 'deleted',
      filterFunction: isIncidentDeleted,
    },
    {
      name: 'empty',
      filterFunction: isIncidentEmpty,
    },
    {
      name: 'emergency',
      filterFunction: isIncidentEmergencyOrBet,
    },
  ].map(reactive)

  const incidentFiltersToggle: ComputedRef<IncidentFilterToggle[]> = computed(
    () => {
      const dynamicFilters: IncidentFilterToggle[] = []
      if (eventClock) {
        dynamicFilters.push(
          reactive({
            name: SameTime,
            filterFunction: hasSameTimeIncidents,
          }),
        )
      }
      if (shouldShowVIPIncidentsFilter.value) {
        dynamicFilters.push(
          reactive({ name: 'VIP incidents', filterFunction: isVIPIncident }),
        )
      }

      if (incidentsCustomFiltersConfig) {
        incidentsCustomFiltersConfig.filters.forEach((filter) => {
          dynamicFilters.push(reactive(filter))
        })
      }

      return [...staticFilters, ...dynamicFilters]
    },
  )

  return {
    incidentFiltersToggle,
  }
}
